<script lang="ts">
  import { api } from '$shared/api';

  interface FeedItem {
    id: string | number;
    feed_type: 'personal' | 'system' | 'community';
    type: string;
    icon: string;
    description: string;
    duel_id: number | null;
    duel_name: string | null;
    track_id: number | null;
    track_name: string | null;
    target_username: string | null;
    target_avatar_url: string | null;
    top_vote: boolean;
    created_at: string;
  }

  interface TrendingTrack {
    id: number;
    name: string | null;
    submitter: string;
    duel_name: string;
    duel_id: number;
    recent_votes: number;
    recent_saves: number;
  }

  interface HiddenGem {
    track_id: number;
    track_name: string;
    track_type: string;
    youtube_id: string;
    username: string;
    vote_count: number;
    yt_view_count: number;
    duel_id: number;
    duel_name: string;
  }

  interface DateGroup {
    label: string;
    items: FeedItem[];
  }

  let items = $state<FeedItem[]>([]);
  let trending = $state<TrendingTrack[]>([]);
  let hiddenGems = $state<HiddenGem[]>([]);
  let loading = $state(true);
  let error = $state('');

  const iconMap: Record<string, string> = {
    vote: '\u2764\uFE0F',
    listen: '\uD83C\uDFA7',
    follow: '\uD83D\uDC64',
    question: '\u2728',
    trophy: '\uD83C\uDFC6',
    gem: '\uD83D\uDC8E',
    join: '\u2795',
  };

  function relativeTime(dateStr: string): string {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diff = Math.max(0, now - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    return new Date(dateStr).toLocaleDateString();
  }

  function dateLabel(dateStr: string): string {
    const d = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const itemDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diff = Math.floor((today.getTime() - itemDay.getTime()) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return d.toLocaleDateString(undefined, { weekday: 'long' });
    return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  }

  const grouped: DateGroup[] = $derived.by(() => {
    const groups: DateGroup[] = [];
    let currentLabel = '';
    for (const item of items) {
      const label = dateLabel(item.created_at);
      if (label !== currentLabel) {
        currentLabel = label;
        groups.push({ label, items: [item] });
      } else {
        groups[groups.length - 1].items.push(item);
      }
    }
    return groups;
  });

  /** Escape HTML entities to prevent XSS */
  function escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /** Parse description to inject SPA hash links (XSS-safe) */
  function linkify(desc: string, item: FeedItem): string {
    let result = escapeHtml(desc);
    if (item.duel_id && item.duel_name) {
      const safeName = escapeHtml(item.duel_name);
      result = result.replace(safeName, `<a href="#/duels/${item.duel_id}">${safeName}</a>`);
    }
    if (item.target_username) {
      const safeUser = escapeHtml(item.target_username);
      result = result.replace(`@${safeUser}`, `<a href="#/users/${safeUser}">@${safeUser}</a>`);
    }
    return result;
  }

  $effect(() => {
    loading = true;
    api
      .get<{ feed: FeedItem[]; trending: TrendingTrack[]; hidden_gems: HiddenGem[] }>('/users/current/feed')
      .then((data) => {
        const seen = new Set<string | number>();
        items = (data.feed ?? []).filter((item) => {
          if (seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        });
        trending = data.trending ?? [];
        hiddenGems = data.hidden_gems ?? [];
      })
      .catch((e) => {
        console.error('Failed to load feed:', e);
        error = e?.message ?? 'Failed to load feed';
      })
      .finally(() => {
        loading = false;
      });
  });
</script>

<div class="feed-page">
  {#if !loading && !error && trending.length > 0}
    <section>
      <h2>Trending Tracks</h2>
      <ol class="trending-list">
        {#each trending as track, i (track.id)}
          <li>
            <a href={`#/tracks/${track.id}`} class="card trending-item">
              <span class="trending-item__rank">{i + 1}</span>
              <div class="trending-item__info">
                <span class="trending-item__name">{track.name || 'Untitled'}</span>
                <span class="trending-item__meta">
                  by {track.submitter} &middot; {track.duel_name}
                </span>
              </div>
              <span class="trending-item__votes">
                {track.recent_votes} votes{track.recent_saves > 0 ? ` · ${track.recent_saves} saves` : ''}
              </span>
            </a>
          </li>
        {/each}
      </ol>
    </section>
  {/if}

  {#if !loading && !error && hiddenGems.length > 0}
    <section class="home-section">
      <h2 class="section-title">Hidden Gems</h2>
      <p class="section-subtitle">Discovered on Beat Duel before the world</p>
      <div class="duel-grid">
        {#each hiddenGems as gem}
          {@const views = Number(gem.yt_view_count || 0)}
          {@const viewLabel = views >= 1000 ? `${(views / 1000).toFixed(1)}K` : `${views}`}
          <a href={gem.duel_id ? `#/duels/${gem.duel_id}` : '#/duels'} class="gem-card card">
            <div class="gem-track">{gem.track_name || 'Untitled'}</div>
            <div class="gem-meta">
              {#if gem.vote_count > 0}
                <span class="gem-votes">{gem.vote_count} BD votes</span>
              {/if}
              <span class="gem-views">{viewLabel} YT views</span>
            </div>
            <div class="gem-submitter">by @{gem.username}</div>
          </a>
        {/each}
      </div>
    </section>
  {/if}

  {#if loading}
    <div class="feed-skeleton">
      {#each Array(6) as _}
        <div class="skeleton-card">
          <div class="skeleton-icon"></div>
          <div class="skeleton-body">
            <div class="skeleton-line skeleton-line--long"></div>
            <div class="skeleton-line skeleton-line--short"></div>
          </div>
        </div>
      {/each}
    </div>
  {:else if error}
    <div class="feed-error">{error}</div>
  {:else if items.length === 0}
    <div class="feed-empty">
      <p>No activity yet. Start a <a href="#/duels">Quick Match</a>!</p>
    </div>
  {:else}
    {#each grouped as group}
      <div class="date-header">{group.label}</div>
      {#each group.items as item (item.id)}
        <div class="feed-card feed-card--{item.feed_type}">
          <span class="feed-icon">{iconMap[item.icon] ?? '\u26A1'}</span>
          <div class="feed-content">
            <p class="feed-desc">{@html linkify(item.description, item)}</p>
            {#if item.top_vote}
              <span class="feed-badge">Top Vote</span>
            {/if}
          </div>
          <span class="feed-time">{relativeTime(item.created_at)}</span>
        </div>
      {/each}
    {/each}
  {/if}
</div>

<style>
  .feed-page {
    max-width: 640px;
    margin: 0 auto;
    padding: 1.5rem 1rem;
  }

  section {
    margin-bottom: 2rem;
  }

  .trending-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .trending-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    text-decoration: none;
  }
  .trending-item__rank {
    font-size: 1.25rem;
    font-weight: 800;
    color: var(--pink);
    min-width: 1.75rem;
    text-align: center;
    flex-shrink: 0;
  }
  .trending-item__info {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
    flex: 1;
  }
  .trending-item__name {
    font-weight: 600;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .trending-item__meta {
    font-size: 0.8125rem;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .trending-item__votes {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--cyan);
    white-space: nowrap;
    flex-shrink: 0;
  }

  .gem-card {
    padding: 1rem;
    text-decoration: none;
    color: inherit;
  }
  .gem-track {
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  .gem-meta {
    display: flex;
    gap: 0.5rem;
    font-size: 0.8rem;
    margin-bottom: 0.25rem;
  }
  .gem-votes {
    color: var(--cyan);
  }
  .gem-views {
    color: var(--text-muted);
  }
  .gem-submitter {
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  /* Date headers */
  .date-header {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted);
    margin: 1.25rem 0 0.5rem;
  }
  .date-header:first-of-type {
    margin-top: 0;
  }

  /* Feed card */
  .feed-card {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    margin-bottom: 0.5rem;
  }
  .feed-card--system {
    background: color-mix(in srgb, var(--cyan) 5%, var(--surface));
  }
  .feed-card--community {
    background: color-mix(in srgb, var(--purple) 6%, var(--surface));
  }

  .feed-icon {
    font-size: 1.25rem;
    flex-shrink: 0;
    width: 2rem;
    text-align: center;
    line-height: 1.4;
  }

  .feed-content {
    flex: 1;
    min-width: 0;
  }

  .feed-desc {
    font-size: 0.875rem;
    line-height: 1.45;
    color: var(--text);
    margin: 0;
  }
  .feed-desc :global(a) {
    color: var(--cyan);
    font-weight: 500;
  }
  .feed-desc :global(a:hover) {
    color: var(--pink);
  }

  .feed-badge {
    display: inline-block;
    margin-top: 0.3rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--pink);
    background: color-mix(in srgb, var(--pink) 12%, transparent);
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
  }

  .feed-time {
    font-size: 0.75rem;
    color: var(--text-muted);
    white-space: nowrap;
    flex-shrink: 0;
    line-height: 1.5;
  }

  /* Empty / error */
  .feed-empty,
  .feed-error {
    text-align: center;
    padding: 3rem 1rem;
    color: var(--text-muted);
    font-size: 0.95rem;
  }
  .feed-error {
    color: var(--error);
  }

  /* Skeleton */
  .feed-skeleton {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .skeleton-card {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }
  .skeleton-icon {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: var(--surface-2);
    animation: pulse 1.2s ease-in-out infinite;
  }
  .skeleton-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }
  .skeleton-line {
    height: 0.75rem;
    border-radius: 4px;
    background: var(--surface-2);
    animation: pulse 1.2s ease-in-out infinite;
  }
  .skeleton-line--long {
    width: 80%;
  }
  .skeleton-line--short {
    width: 40%;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.4;
    }
    50% {
      opacity: 0.8;
    }
  }
</style>
